import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Button,
  Card,
  CardActions,
  CardOverflow,
  Divider,
  FormControl,
  FormHelperText,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/joy'
import { Info, InfoOutlined } from '@mui/icons-material'
import { getAutocompleteUsers } from 'features/autocompleteSlice'
import { adminGetUserAvailableTasks } from 'features/billingsV2Slice'
import { getAdminUserBillingTasksHeaders } from 'utils/headers/getAdminUserBillingTasksHeaders'
import usePagination from 'hooks/usePagination'
import newBillingSchema from 'validations/billings/newBillingSchema'
import CustomAutocompleteControl from 'components/forms/CustomAutocompleteControl'
import PageLoader from 'components/PageLoader'
import EnchantedTable from 'components/tables/EnchantedTable'

const BillingFormSubView = ({ handleSubmit, initialValues, loading = false }) => {
  const dispatch = useDispatch()
  const pagination = usePagination()

  const { usersList, loading: autocompleteLoading } = useSelector((s) => s.autocomplete)
  const { userTasks, loading: billingsLoading } = useSelector((s) => s.billingsV2)

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: newBillingSchema,
    onSubmit: handleSubmit,
  })

  useEffect(() => {
    dispatch(getAutocompleteUsers())
  }, [])

  useEffect(() => {
    if (formik.values.user) dispatch(adminGetUserAvailableTasks(formik.values.user.id))
    else formik.resetForm()
  }, [formik.values.user])

  if (autocompleteLoading) return <PageLoader />

  return (
    <form onSubmit={formik.handleSubmit}>
      <Card
        sx={{
          display: 'flex',
          maxWidth: '800px',
          width: '100%',
          mx: 'auto',
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ mb: 1 }}>
          <Typography level="title-md" startDecorator={<Info />} color="primary">
            Uzupełnij dane do rozliczenia
          </Typography>
        </Box>
        <Divider />

        <Stack direction="column" spacing={2} sx={{ mt: 1, mb: 2 }}>
          <CustomAutocompleteControl name="user" label="Wybierz użytkownika" options={usersList} formik={formik} />

          {billingsLoading && <LinearProgress sx={{ my: 2 }} />}

          {formik.values.user && (
            <>
              <Typography level="title-sm">Wybierz zadania do rozliczenia</Typography>
              <EnchantedTable
                headers={getAdminUserBillingTasksHeaders()}
                data={userTasks}
                offset={(pagination.values.currentPage - 1) * pagination.values.perPage}
                noDataText="Brak zadań do wyświetlenia."
                onSelect={(tasks) =>
                  formik.setFieldValue(
                    'taskIds',
                    tasks.map((t) => t.id)
                  )
                }
                sx={{ minWidth: '100%', backgroundColor: 'background.body' }}
              />
              {formik.touched.taskIds && formik.errors.taskIds && (
                <FormControl error={formik.touched.taskIds && Boolean(formik.errors.taskIds)} sx={{ mt: 0, mb: 1 }}>
                  <FormHelperText sx={{ gap: 1 }}>
                    <InfoOutlined fontSize="small" />
                    {formik.errors.taskIds}
                  </FormHelperText>
                </FormControl>
              )}
            </>
          )}
        </Stack>

        <CardOverflow sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
          <CardActions sx={{ alignSelf: 'flex-end', pt: 2 }}>
            <Button onClick={() => formik.resetForm()} size="sm" variant="outlined" color="neutral" disabled={loading}>
              Resetuj
            </Button>
            <Button type="submit" disabled={!formik.dirty} size="sm" variant="solid" loading={loading}>
              Utwórz rozliczenie
            </Button>
          </CardActions>
        </CardOverflow>
      </Card>
    </form>
  )
}

export const newBillingShape = PropTypes.shape({
  userId: PropTypes.string,
  taskIds: PropTypes.arrayOf(PropTypes.string).isRequired,
})

BillingFormSubView.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  initialValues: newBillingShape,
  loading: PropTypes.bool,
}

export default BillingFormSubView
